import { toSafeObject as toObject, toSafeString } from '@shared/utils';
export const VOICE_TRANSCRIPTION_PROVENANCE_SOURCE = 'desktop_dictation';
export const VOICE_TRANSCRIPTION_PROVENANCE_DETECTOR_VERSION = 'token_overlap_v1';

const PROVENANCE_CHUNK_HISTORY_LIMIT = 24;
const MIN_OVERLAP_FOR_SMALL_CHUNK = 2;
const MIN_OVERLAP_FOR_MEDIUM_CHUNK = 3;
const MIN_OVERLAP_FOR_LARGE_CHUNK = 4;
const STOP_WORDS = new Set(['a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'in', 'is', 'it', 'of', 'on', 'or', 'that', 'the', 'to', 'was', 'were', 'with']);

const toSafeBoolean = (value, fallback = false) => {
  if (typeof value === 'boolean') {
    return value;
  }

  const normalized = toSafeString(value).toLowerCase();

  if (!normalized) {
    return fallback;
  }

  if (['1', 'true', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'off'].includes(normalized)) {
    return false;
  }

  return fallback;
};

const normalizeProvenanceToken = (value, fallback) => {
  const normalized = toSafeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);

  if (normalized) {
    return normalized;
  }

  return fallback;
};

const toBoundedPositiveInteger = (value, fallback = 1) => {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed < 1) {
    return fallback;
  }

  return Math.min(500, Math.floor(parsed));
};

const tokenizeForOverlap = (value) => {
  const normalized = toSafeString(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ');

  if (!normalized) {
    return [];
  }

  return normalized
    .split(/\s+/)
    .map((token) => {
      return token.trim();
    })
    .filter((token) => {
      if (!token) {
        return false;
      }

      if (token.length === 1 && !/\d/.test(token)) {
        return false;
      }

      return !STOP_WORDS.has(token);
    });
};

const toTokenCounts = (tokens = []) => {
  const counts = new Map();

  for (const token of tokens) {
    counts.set(token, (counts.get(token) || 0) + 1);
  }

  return counts;
};

const countTokenOverlap = ({ promptTokens, chunkTokens }) => {
  const promptCounts = toTokenCounts(promptTokens);
  const chunkCounts = toTokenCounts(chunkTokens);
  let overlap = 0;

  for (const [token, count] of chunkCounts.entries()) {
    const promptCount = promptCounts.get(token) || 0;

    if (promptCount < 1) {
      continue;
    }

    overlap += Math.min(count, promptCount);
  }

  return overlap;
};

const doesChunkSurvive = ({ promptTokens, chunkTokens }) => {
  const chunkTokenCount = Array.isArray(chunkTokens) ? chunkTokens.length : 0;

  if (chunkTokenCount < 1 || !Array.isArray(promptTokens) || promptTokens.length < 1) {
    return false;
  }

  const overlap = countTokenOverlap({
    promptTokens,
    chunkTokens
  });

  if (overlap < 1) {
    return false;
  }

  if (chunkTokenCount <= 3) {
    return overlap >= MIN_OVERLAP_FOR_SMALL_CHUNK;
  }

  if (chunkTokenCount <= 6) {
    return overlap >= MIN_OVERLAP_FOR_MEDIUM_CHUNK && overlap / chunkTokenCount >= 0.5;
  }

  return overlap >= MIN_OVERLAP_FOR_LARGE_CHUNK && overlap / chunkTokenCount >= 0.4;
};

const normalizeChunks = (chunks = []) => {
  const safeChunks = Array.isArray(chunks) ? chunks : [];
  const normalized = [];

  for (const candidate of safeChunks) {
    const source = normalizeProvenanceToken(candidate?.source, VOICE_TRANSCRIPTION_PROVENANCE_SOURCE);
    const tokens = Array.isArray(candidate?.tokens) ? candidate.tokens.map((token) => toSafeString(token).toLowerCase()).filter(Boolean) : [];

    if (tokens.length < 1) {
      continue;
    }

    normalized.push({
      source,
      tokens
    });
  }

  return normalized.slice(-PROVENANCE_CHUNK_HISTORY_LIMIT);
};

export const isVoiceTranscriptionProvenanceHintEnabled = ({ env = import.meta?.env } = {}) => {
  return toSafeBoolean(env?.VITE_PIEVE_VOICE_TRANSCRIPTION_PROVENANCE_HINT_ENABLED, true);
};

export const appendVoiceTranscriptionChunk = ({ chunks, transcription, source = VOICE_TRANSCRIPTION_PROVENANCE_SOURCE } = {}) => {
  if (!isVoiceTranscriptionProvenanceHintEnabled()) {
    return [];
  }

  const tokens = tokenizeForOverlap(transcription);

  if (tokens.length < 1) {
    return normalizeChunks(chunks);
  }

  const nextChunks = normalizeChunks(chunks).concat([
    {
      source: normalizeProvenanceToken(source, VOICE_TRANSCRIPTION_PROVENANCE_SOURCE),
      tokens
    }
  ]);

  return nextChunks.slice(-PROVENANCE_CHUNK_HISTORY_LIMIT);
};

export const normalizeVoiceTranscriptionInputProvenance = ({ inputProvenance } = {}) => {
  if (!isVoiceTranscriptionProvenanceHintEnabled()) {
    return null;
  }

  const provenanceObject = toObject(inputProvenance);
  const voiceTranscription = toObject(provenanceObject?.voiceTranscription || provenanceObject?.voice_transcription);
  const present = toSafeBoolean(voiceTranscription.present, false);

  if (present !== true) {
    return null;
  }

  return {
    voiceTranscription: {
      present: true,
      source: normalizeProvenanceToken(voiceTranscription.source, VOICE_TRANSCRIPTION_PROVENANCE_SOURCE),
      detectorVersion: normalizeProvenanceToken(voiceTranscription.detectorVersion || voiceTranscription.detector_version, VOICE_TRANSCRIPTION_PROVENANCE_DETECTOR_VERSION),
      chunkCount: toBoundedPositiveInteger(voiceTranscription.chunkCount || voiceTranscription.chunk_count, 1)
    }
  };
};

export const resolveVoiceTranscriptionInputProvenance = ({ prompt, chunks } = {}) => {
  if (!isVoiceTranscriptionProvenanceHintEnabled()) {
    return null;
  }

  const normalizedChunks = normalizeChunks(chunks);

  if (normalizedChunks.length < 1) {
    return null;
  }

  const promptTokens = tokenizeForOverlap(prompt);

  if (promptTokens.length < 1) {
    return null;
  }

  let survivingChunkCount = 0;
  let source = VOICE_TRANSCRIPTION_PROVENANCE_SOURCE;

  for (const chunk of normalizedChunks) {
    if (!doesChunkSurvive({ promptTokens, chunkTokens: chunk.tokens })) {
      continue;
    }

    survivingChunkCount += 1;
    source = normalizeProvenanceToken(chunk.source, VOICE_TRANSCRIPTION_PROVENANCE_SOURCE);
  }

  if (survivingChunkCount < 1) {
    return null;
  }

  return {
    voiceTranscription: {
      present: true,
      source,
      detectorVersion: VOICE_TRANSCRIPTION_PROVENANCE_DETECTOR_VERSION,
      chunkCount: survivingChunkCount
    }
  };
};
